import { z } from 'zod';

import {
  BaseFlowBlockSchema,
  BaseNotableObjectConfigSchema,
  BlockType,
  NotableObject,
} from '../base/baseSchema.js';
import { BlockValueEmailSchema } from '../base/blockValueEmailSchema.js';
import { VoiceCallModel } from '../voiceCall/voiceCallBlock.js';

export const UpdatePatientOrderConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.PatientOrder),
  patientOrderId: z.string(),
  status: z.string().optional(),
  requestedAppointmentTimePeriodStart: z.string().nullish(),
  requestedAppointmentTimePeriodEnd: z.string().nullish(),
  procedures: z
    .array(
      z
        .object({
          description: z.string(),
          cptCode: z.string().nullish(),
          areaOfInterest: z.string().nullish(),
        })
        .strip()
    )
    .optional(),
  diagnoses: z
    .array(
      z.object({
        icd10Codes: z.array(z.string()).nullish(),
        description: z.string(),
      })
    )
    .optional(),
  specialty: z.string().nullish(),
  referredByDepartment: z.string().nullish(),
  referredToDepartment: z.string().nullish(),
  reasonForExam: z.string().nullish(),
  referredByEhrProviderFirstName: z.string().optional(),
  referredByEhrProviderLastName: z.string().optional(),
  referredByEhrProviderValue: z.string().optional(),
  referredByEhrProviderEmail: z.string().optional(),
  referredToEhrProviderFirstName: z.string().optional(),
  referredToEhrProviderLastName: z.string().optional(),
  referredToEhrProviderValue: z.string().optional(),
  referredToEhrProviderEmail: z.string().optional(),
  ehrProviderKind: z.string().optional(),
});

export const UpdatePaymentFeeType = z.enum(['copay']);
export const UpdatePaymentConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Payment),
  encounterId: z.string(),
  feeType: UpdatePaymentFeeType,
  feeAmount: z.string(),
});

export const UpdateProviderConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Provider),
  ehrId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  npi: z.string().optional(),
  email: BlockValueEmailSchema({ isRequired: true }),
  ehrKind: z.string(),
});

export const UpdateEncounterConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Encounter),
  payload: z.string(),
});

export const UpdatePatientConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Patient),
  payload: z.string(),
});

export const UpdateAssistantConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Assistant),
  assistantId: z.string(),
  model: z.nativeEnum(VoiceCallModel),
  prompt: z.string(),
  firstMessage: z.string().optional(),
});

export const UpdatePatientInsuranceInformationConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.PatientInsuranceInformation),
  patientInsuranceInformartionId: z.string(),
  planName: z.string(),
});

export const UpdateNotableObjectConfigSchema = z.discriminatedUnion('notableObject', [
  UpdatePatientOrderConfigSchema,
  UpdatePaymentConfigSchema,
  UpdateProviderConfigSchema,
  UpdateEncounterConfigSchema,
  UpdatePatientConfigSchema,
  UpdateAssistantConfigSchema,
  UpdatePatientInsuranceInformationConfigSchema,
]);

export type UpdateNotableObjectConfig = z.infer<typeof UpdateNotableObjectConfigSchema>;

export const UpdateNotableObjectBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.UpdateNotableObject),
  config: UpdateNotableObjectConfigSchema,
});

export type UpdateNotableObjectBlock = z.infer<typeof UpdateNotableObjectBlockSchema>;
